import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';
import { TCPOffsetOptionStrict } from '@sb/motion-planning';

// Sentinel user/task IDs and synthetic hardware-config ID recognised across the
// UI, step validators, routine runner, and echo-bot. Centralised so a rename
// can't go stale. Keep in sync with python/apps/echo-bot/src/constants.py.
export const DEMONSTRATION_USER_ID_SENTINEL = 'demonstration';
export const DEMONSTRATION_TASK_ID_SENTINEL = 'demonstration';
export const DEMONSTRATION_SYNTHETIC_HARDWARE_CONFIG_ID =
  'demonstration-synthetic';

// `rotation` is an axis-angle rotation vector (NOT a quaternion) matching
// echo-bot's RO1PoseRecorder JSON output.
export const DemonstrationPoseFrame = zod.object({
  timestamp: zod.number(),
  frame_number: zod.number().int(),
  translation: zod.object({
    x: zod.number(),
    y: zod.number(),
    z: zod.number(),
  }),
  rotation: zod.object({
    x: zod.number(),
    y: zod.number(),
    z: zod.number(),
  }),
});

export type DemonstrationPoseFrame = zod.infer<typeof DemonstrationPoseFrame>;

export const PREVIEW_PATH_MAX_POINTS = 200;

// Downsample pose frames to a small position-only polyline for skill-detail
// list thumbnails. `rotvecFrameToPose` only re-expresses orientation —
// translation passes through unchanged — so a frame's raw translation is the
// exact point the path viz renders. Computed once at create time and stored on
// the row so the list can `$select` metadata + this and skip the heavy `data`
// (frames) column entirely.
export function decimatePosePath(
  frames: ReadonlyArray<DemonstrationPoseFrame>,
  maxPoints = PREVIEW_PATH_MAX_POINTS,
): Array<[number, number, number]> {
  if (frames.length === 0) return [];

  const stride = Math.max(1, Math.ceil(frames.length / maxPoints));
  const points: Array<[number, number, number]> = [];

  // Reserve the final slot for the endpoint append below: stride sampling of a
  // length that's an exact multiple of maxPoints would otherwise fill all
  // maxPoints slots and then push one more, so the real bound becomes
  // maxPoints. Capping the loop at maxPoints - 1 keeps the total ≤ maxPoints.
  for (
    let i = 0;
    i < frames.length && points.length < maxPoints - 1;
    i += stride
  ) {
    const { x, y, z } = frames[i]!.translation;
    points.push([x, y, z]);
  }

  // Always keep the endpoint so the polyline closes on the true final pose.
  const last = frames[frames.length - 1]!.translation;
  const tail = points[points.length - 1];

  if (!tail || tail[0] !== last.x || tail[1] !== last.y || tail[2] !== last.z) {
    points.push([last.x, last.y, last.z]);
  }

  return points;
}

// Each gripper JSONL line: `{ ts, gp_0, to_0?, gp_1?, to_1?, ... }`. Single-
// gripper recordings always carry `gp_0`; `to_*` (trigger) is leader-side and
// may be absent. `.passthrough()` keeps multi-gripper fields.
export const DemonstrationGripperFrame = zod
  .object({
    ts: zod.number(),
    gp_0: zod.number(),
    to_0: zod.number().optional(),
  })
  .passthrough();

export type DemonstrationGripperFrame = zod.infer<
  typeof DemonstrationGripperFrame
>;

// Verbatim recorder `metadata.json` so a recording is self-describing.
// `gripper_tcp_position`/`gripper_tcp_rotation` are the record-time gripper
// TCP offset (m / rad, XYZ euler), used at replay to compose the frame
// transform.
export const DemonstrationMetadata = zod
  .object({
    gripper_tcp_position: zod.array(zod.number()).nullable().optional(),
    gripper_tcp_rotation: zod.array(zod.number()).nullable().optional(),
  })
  .passthrough();

export type DemonstrationMetadata = zod.infer<typeof DemonstrationMetadata>;

// On-disk trajectory artifacts mirrored onto the row's `data` field. The
// session `metadata.json` is a top-level sibling (matches cloud upload).
export const DemonstrationFilesBundle = zod.object({
  pose: zod.object({
    robot_name: zod.string(),
    frames: zod.array(DemonstrationPoseFrame),
  }),
  gripper: zod
    .object({
      frames: zod.array(DemonstrationGripperFrame),
    })
    .nullable()
    .optional(),
});

export type DemonstrationFilesBundle = zod.infer<
  typeof DemonstrationFilesBundle
>;

// Optional "follow reference" anchor: the reference's world pose at record
// time, replayed against the same reference picked again at runtime. Absent
// ⇒ world-frame recording.
export const DemonstrationReferenceFrame = zod.object({
  refWorldPose: CartesianPose,
  // tcpOption the reference was saved in (`'wrist'`, `'ee-*'`, …). Used to
  // align the "follow reference" preview to the same tooltip/gripper-tip
  // point the reference was recorded against. Absent ⇒ wrist.
  refTcpOption: zod.string().optional(),
});

export type DemonstrationReferenceFrame = zod.infer<
  typeof DemonstrationReferenceFrame
>;

// The Active Tooltip the recording was captured against, resolved across the
// whole device chain the tooltip names. Replay re-expresses the recorded path
// from this frame onto the tooltip mounted then. Absent ⇒ the recording
// carries no tool frame and replays in the frame it was captured in.
export const DemonstrationToolFrame = zod.object({
  tcpOffset: CartesianPose,
  // Which tooltip that offset belongs to, so a replay can name and re-resolve
  // it rather than only compose its geometry. Two same-kind grippers share a
  // `tcpOption`, so the device id is what tells them apart.
  //
  // Strict + optional, not the `.catch('wrist')` variant: that one cannot be
  // absent, so a recording whose tooltip was never named would parse to the
  // wrist and a 'saved' replay would resolve identity — displacing the path by
  // the tool offset the row still carries.
  tcpOption: TCPOffsetOptionStrict.optional(),
  gripperID: zod.string().optional(),
});

export type DemonstrationToolFrame = zod.infer<typeof DemonstrationToolFrame>;

export const DemonstrationRecordingData = zod.object({
  id: zod.string(),
  name: zod.string(),
  durationSeconds: zod.coerce.number().default(0),
  frameCount: zod.number().int().default(0),
  hasGripper: zod.boolean().default(false),
  schemaVersion: zod.number().int().default(1),
  // Nullable + optional: world-frame recording ⇒ `null`.
  referenceFrame: DemonstrationReferenceFrame.nullable().optional(),
  // Nullable + optional: no tool frame recorded ⇒ `null`.
  toolFrame: DemonstrationToolFrame.nullable().optional(),
  // Auto-detected idle-trim bounds (recorder seconds). Coerced because the
  // DECIMAL column arrives as a string from postgres.
  firstMotionTimestamp: zod.coerce.number().nullable().optional(),
  lastMotionTimestamp: zod.coerce.number().nullable().optional(),
  data: zod.object({ files: DemonstrationFilesBundle }),
  metadata: DemonstrationMetadata.nullable().optional(),
  // ≤200-pt position-only polyline precomputed at create for list thumbnails.
  // Nullable + optional: pre-migration rows have none and fall back to
  // decimating `data` client-side.
  previewPath: zod
    .array(zod.tuple([zod.number(), zod.number(), zod.number()]))
    .nullable()
    .optional(),
  createdAt: zod.string().optional(),
  updatedAt: zod.string().optional(),
});

export type DemonstrationRecordingData = zod.infer<
  typeof DemonstrationRecordingData
>;
